import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { push_undo } from 'data/lib/undo'
import { diff_obj } from 'data/lib/diff'
import { PromiseWatch } from "./lib/promise-helpers"
import validationService from "data/validate"

// private module logic //

const id: string = "cashFlowMap"
const initialState: ApiResult<object[]> = { id, state: "init", value: [] }
const cashFlowMapStream = new BehaviorSubject<ApiResult<object[]>>(initialState)
const cashFlowMapEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let cashFlowMapMap = {}
const _patches_ = PromiseWatch()

const current = () => cashFlowMapStream.getValue()

function clear(): void {
  cashFlowMapStream.next({ id, state: 'ok', value: [] })
  cashFlowMapMap = {}
}

function get(cashFlowMapMapId: number): object {
  return cashFlowMapMap[cashFlowMapMapId]
}

function sortCashFlowMaps(data: object[]): object[] {

  data.sort((a, b) => {
    let A = a["cashflowlineitem"], Ap = a["summaryaccountid"], B = b["cashflowlineitem"], Bp = b["summaryaccountid"]
    if (A !== B){
      return A < B ? -1 : 1
    } else {
      return (!Bp || Ap < Bp) ? -1 : 1
    }
  });

  return data
}

function fetchCashFlowMaps(chartid:number): Promise<object[] | { message: string }> {
  clear()

  return Client.get(`cashflowmap?chartid=eq.${chartid}`)
  .then((records: object[]) => {
    cashFlowMapStream.next({ id, state: 'ok', value: sortCashFlowMaps(records) })
    validationService.fetchByParent('cashflowmap', 'chart', chartid)
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function patch(cashFlowMap: {
  cashflowmapid: number, chartid: number
}): Promise<object | { message: string }> {
  const streamVal: object[] = cashFlowMapStream.getValue().value
  const cashflowmapid: number = cashFlowMap.cashflowmapid
  const streamValCashFlowMap = _.find(streamVal, { cashflowmapid })
  const changeset = diff_obj(cashFlowMap, streamValCashFlowMap)
  if (_.isEmpty(changeset)) return new Promise(() => streamVal)

  push_undo({
    undofunc: () => patch(streamValCashFlowMap),
    redofunc: () => patch(cashFlowMap)
  })

  const newStreamVal = _.cloneDeep(streamVal)
  newStreamVal.splice(_.findIndex(streamVal, a => a.cashflowmapid === cashflowmapid), 1, cashFlowMap)

  // optimistic update
  cashFlowMapStream.next({
    id,
    state: 'ok',
    value: newStreamVal
  })

  return _patches_.watch(cashflowmapid, Client.patch(`cashflowmap?cashflowmapid=eq.${cashflowmapid}`, changeset)
    .then((cashFlowMap: {cashflowmapid: number}) => {
      cashFlowMapMap[cashFlowMap.cashflowmapid] = cashFlowMap
      validationService.fetch('cashflowmap', cashFlowMap.cashflowmapid)
      return cashFlowMap
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      cashFlowMapStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchCashFlowMaps(cashFlowMap.chartid)
      return err
    }))

}

// public module interface //

const cashFlowMapService = {
  stream: () => cashFlowMapStream.asObservable(),
  value: () => cashFlowMapStream.getValue().value,
  eventStream: () => cashFlowMapEventStream.asObservable(),
  clear,
  current,
  get,
  fetchCashFlowMaps,
  patch
}

export default cashFlowMapService
